import { NetworkStatus } from "@apollo/client"
import { ArrowBack, CheckCircle, Inventory, Lock, QrCodeScanner, Summarize } from "@mui/icons-material"
import {
  Alert,
  alpha,
  Box,
  Button,
  Container,
  IconButton,
  Paper,
  Step,
  StepLabel,
  Stepper,
  styled,
  Typography,
  useTheme
} from "@mui/material"
import React, { useEffect, useMemo, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { toast } from "react-toastify"
import {
  AuditProductStatus,
  useCloseAuditMutation,
  useGetAuditQuery,
  useStartAuditMutation
} from "src/graphql/quark/quark.type"
import ROUTE_PATHS from "src/routes/route-path"
import { Conditional, PageLoader, Stack } from "src/shared/components"
import OutlinedInput from "src/shared/components/outlined-input"
import ConfirmationDialog from "src/shared/container/confirmation-dialog"
import AuditConfirmation from "./components/AuditConfirmation"
import AuditSummary from "./components/AuditSummary"
import LotScanning from "./components/LotScanning"
import ProductSelection from "./components/ProductSelection"
import { useAuditExecution } from "./hooks/useAuditExecution"
import { AuditExecutionState, AuditProduct } from "./types"

// Styled containers for smooth step transitions
const StepContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isActive"
})<{ isActive: boolean }>(({ isActive }) => ({
  display: isActive ? "block" : "none",
  minHeight: "500px",
  animation: isActive ? `slideInFade 0.4s ease-in-out` : undefined,
  "@keyframes slideInFade": {
    from: {
      opacity: 0,
      transform: "translateX(20px)"
    },
    to: {
      opacity: 1,
      transform: "translateX(0)"
    }
  }
}))

// Enhanced page container with gradient background
const PageContainer = styled(Box)(({ theme }) => ({
  minHeight: "100vh",
  background: `linear-gradient(135deg, 
    ${alpha(theme.palette.primary.main, 0.02)} 0%, 
    ${alpha(theme.palette.secondary.main, 0.02)} 50%, 
    ${alpha(theme.palette.primary.main, 0.01)} 100%)`,
  marginTop: theme.spacing(3)
  // paddingBottom: theme.spacing(3)
}))

// Glass-morphism card container
const ContentCard = styled(Paper)(({ theme }) => ({
  borderRadius: theme.spacing(2),
  padding: theme.spacing(4),
  background: `linear-gradient(145deg, 
    ${alpha(theme.palette.background.paper, 0.95)}, 
    ${alpha(theme.palette.background.default, 0.9)})`,
  backdropFilter: "blur(10px)",
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.1)}`,
  position: "relative",
  overflow: "hidden",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "4px",
    background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`
  }
}))

const steps = [
  {
    label: "Select Product",
    icon: <Inventory />,
    key: "SELECT_PRODUCT",
    description: "Choose a product to audit"
  },
  {
    label: "Scan Lots",
    icon: <QrCodeScanner />,
    key: "SCAN_LOTS",
    description: "Scan and record lot information"
  },
  {
    label: "Review Summary",
    icon: <Summarize />,
    key: "SHOW_SUMMARY",
    description: "Review audit results"
  },
  {
    label: "Confirm",
    icon: <CheckCircle />,
    key: "CONFIRM",
    description: "Complete the audit process"
  }
]

const AuditExecutionPage: React.FC = () => {
  const navigate = useNavigate()
  const theme = useTheme()
  const { id: auditId } = useParams() // This could come from route params
  const [auditProductToStartConfirmation, setAuditProductToStartConfirmation] = useState<AuditProduct | null>(null)
  const [isOpenCloseAuditConfirmationDialog, setIsOpenCloseAuditConfirmationDialog] = useState(false)
  const { state, setStep, selectProduct, submitAudit, confirmAndContinue, resetState } = useAuditExecution(
    auditId as string
  )
  const [closeAuditReason, setCloseAuditReason] = useState("")
  const {
    data: auditResp,
    loading: isAuditLoading,
    refetch: refetchAudit,
    networkStatus
  } = useGetAuditQuery({
    fetchPolicy: "network-only",
    variables: {
      auditId: auditId as string
    },
    notifyOnNetworkStatusChange: true,
    skip: !auditId
  })
  const auditLoading = isAuditLoading || networkStatus === NetworkStatus.refetch

  // Reset state when component mounts
  useEffect(() => {
    resetState()
  }, [resetState])

  useEffect(() => {
    if (state.isSubmitted) {
      refetchAudit()
    }
  }, [state.isSubmitted, refetchAudit])
  const handleGoBack = () => {
    navigate(ROUTE_PATHS.AUDIT.BASE)
  }

  const handleComplete = () => {
    resetState()
    handleGoBack()
  }
  const auditProducts = useMemo(() => {
    return (
      auditResp?.getAudit?.auditProducts?.map((auditProduct) => ({
        id: auditProduct.id,
        name: auditProduct.product.name,
        uomFactor: auditProduct.product.uomFactor,
        secondaryId: auditProduct.product.secondaryId,
        lotCount: auditProduct.lotCount,
        status: auditProduct.status
      })) ?? ([] as AuditProduct[])
    )
  }, [auditResp?.getAudit?.auditProducts])
  const isSomeOfAuditProductInProgress = useMemo(() => {
    return auditResp?.getAudit?.auditProducts?.some((ap) => ap.status === AuditProductStatus.InProgress) ?? false
  }, [auditResp?.getAudit?.auditProducts])

  const handleSelectProduct = (product: AuditProduct) => {
    switch (product.status) {
      case AuditProductStatus.Pending:
        setAuditProductToStartConfirmation(product)
        break
      case AuditProductStatus.InProgress:
        selectProduct(product)
        break
      default:
        break
    }
  }

  const [startAudit, { loading: startAuditLoading }] = useStartAuditMutation()
  const [closeAudit, { loading: closeAuditLoading }] = useCloseAuditMutation()
  const getCurrentStepIndex = () => {
    return steps.findIndex((step) => step.key === state.currentStep)
  }
  const currentStep = steps[getCurrentStepIndex()]
  const handlePreviousStep = () => {
    const currentStepIndex = getCurrentStepIndex()
    if (currentStepIndex > 0) {
      setStep(steps[currentStepIndex - 1].key as AuditExecutionState["currentStep"])
    }
  }
  const handleNextStep = () => {
    const currentStepIndex = getCurrentStepIndex()
    if (currentStepIndex > 0) {
      setStep(steps[currentStepIndex + 1].key as AuditExecutionState["currentStep"])
    }
  }

  if (auditLoading)
    return (
      <Box sx={{ height: "100vh", position: "relative" }}>
        <PageLoader height={"100%"} position={"absolute"} isLoading={true} />
      </Box>
    )

  return (
    <>
      <PageContainer>
        <Container maxWidth="lg">
          {/* Header with Breadcrumbs */}
          <Box sx={{ mb: 4 }}>
            <Box display="flex" alignItems="center" gap={2} mb={2}>
              <IconButton
                onClick={handleGoBack}
                sx={{
                  backgroundColor: alpha(theme.palette.background.paper, 0.8),
                  backdropFilter: "blur(10px)",
                  "&:hover": {
                    backgroundColor: alpha(theme.palette.primary.main, 0.1)
                  }
                }}
              >
                <ArrowBack />
              </IconButton>

              <Box>
                <Typography variant="h4" fontWeight="bold" color="primary.main">
                  Audit Execution
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  ID: {auditId}
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Progress Stepper */}
          <ContentCard sx={{ mb: 4 }}>
            <Box>
              <Stack variant="rowCenterSpaceBetween" spacing={2} mb={2}>
                <Typography variant="h6" gutterBottom color="primary.main">
                  Progress
                </Typography>
                <Button
                  onClick={() => setIsOpenCloseAuditConfirmationDialog(true)}
                  size="small"
                  color="primary"
                  disabled={isSomeOfAuditProductInProgress}
                  variant="contained"
                  startIcon={<Lock fontSize="small" />}
                >
                  Close Audit
                </Button>
              </Stack>

              <Stepper activeStep={getCurrentStepIndex()} alternativeLabel>
                {steps.map((step, index) => (
                  <Step key={step.key}>
                    <StepLabel
                      icon={step.icon}
                      sx={{
                        "& .MuiStepLabel-label": {
                          fontWeight: index === getCurrentStepIndex() ? 600 : 400
                        }
                      }}
                    >
                      <Box textAlign="center">
                        <Typography variant="body2" fontWeight="inherit">
                          {step.label}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {step.description}
                        </Typography>
                      </Box>
                    </StepLabel>
                  </Step>
                ))}
              </Stepper>
            </Box>
          </ContentCard>

          {/* Error Alert */}
          {state.error && (
            <Alert
              severity="error"
              sx={{
                mb: 3,
                borderRadius: 2,
                backdropFilter: "blur(10px)"
              }}
            >
              {state.error}
            </Alert>
          )}

          {/* Main Content Area */}
          <ContentCard sx={{ mb: 4 }}>
            <Conditional condition={state.currentStep !== "SELECT_PRODUCT"}>
              <Box display="flex" alignItems="center" gap={2} mb={2}>
                <Box>
                  <Typography variant="h5" gutterBottom color="primary.main">
                    {currentStep?.label}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    {currentStep?.description}
                  </Typography>
                </Box>
              </Box>
            </Conditional>

            {/* Step Content */}
            <StepContainer isActive={state.currentStep === "SELECT_PRODUCT"}>
              <ProductSelection
                label={currentStep?.label}
                description={currentStep?.description}
                products={auditProducts}
                onSelectProduct={handleSelectProduct}
              />
            </StepContainer>

            <StepContainer isActive={state.currentStep === "SCAN_LOTS"}>
              {state.selectedProduct && <LotScanning onContinue={handleNextStep} product={state.selectedProduct} />}
            </StepContainer>

            <StepContainer isActive={state.currentStep === "SHOW_SUMMARY"}>
              {state.selectedProduct?.id && (
                <AuditSummary
                  onStepBack={handlePreviousStep}
                  product={state.selectedProduct}
                  onSubmit={submitAudit}
                  isLoading={state.isLoading}
                />
              )}
            </StepContainer>

            <StepContainer isActive={state.currentStep === "CONFIRM"}>
              {state.selectedProduct && (
                <AuditConfirmation
                  product={state.selectedProduct}
                  onContinue={confirmAndContinue}
                  onComplete={handleComplete}
                />
              )}
            </StepContainer>
          </ContentCard>
        </Container>
      </PageContainer>
      <ConfirmationDialog
        open={!!auditProductToStartConfirmation}
        loading={startAuditLoading}
        toggleDialog={() => setAuditProductToStartConfirmation(null)}
        closeOnConfirm={false}
        onConfirm={async () => {
          if (!auditProductToStartConfirmation) return
          const resp = await startAudit({
            variables: {
              auditId: auditId as string,
              productId: auditProductToStartConfirmation.id as string
            }
          })
          if (resp.data?.startAudit) {
            selectProduct(auditProductToStartConfirmation as AuditProduct)
            setAuditProductToStartConfirmation(null)
          }
        }}
        subtitle={`Are you sure you want to start the audit for ${auditProductToStartConfirmation?.name}?`}
      />

      <ConfirmationDialog
        open={!!isOpenCloseAuditConfirmationDialog}
        toggleDialog={() => {
          setIsOpenCloseAuditConfirmationDialog(false)
          setCloseAuditReason("")
        }}
        closeOnConfirm={false}
        onConfirm={async () => {
          if (!isOpenCloseAuditConfirmationDialog) return
          const resp = await closeAudit({
            variables: { auditId: auditId as string, closureReason: closeAuditReason }
          })
          if (resp.data?.closeAudit) {
            setIsOpenCloseAuditConfirmationDialog(false)
            toast.success(`Audit ${auditResp?.getAudit?.secondaryId} has been Closed Successfully.`)
            navigate(ROUTE_PATHS.AUDIT.BASE)
          }
        }}
        subtitle={
          <Stack>
            <Typography variant="body2">You want to close audit {auditResp?.getAudit?.secondaryId}?</Typography>
            <OutlinedInput
              label="Reason"
              placeholder="Enter reason"
              type="text"
              multiline
              rows={2}
              fullWidth
              value={closeAuditReason}
              onChange={(e) => setCloseAuditReason(e.target.value)}
            />
          </Stack>
        }
        loading={closeAuditLoading}
      />
    </>
  )
}

export default AuditExecutionPage
